export type MealPlan = '3' | '5';

export interface NutritionTargets {
  calorie_target: number;
  protein_target_g: string;
  fat_target_g:     string;
  carb_target_g:    string;
  fiber_target_g:   string;
}

export interface UserProfile {
  birth_year?: number; gender?: string; height_cm?: number; weight_kg?: number;
  activity_level: string; goal: string;
  calorie_target?: number;
  protein_target_g?: string | null;
  fat_target_g?:     string | null;
  carb_target_g?:    string | null;
  fiber_target_g?:   string | null;
  meal_plan_type?: MealPlan;
  targets_calculated?: NutritionTargets | null;
  targets_meta?: TargetsMeta;
  // MG_504_V_types
  bedtime_hour?: number | null;
  cheat_meal_interval?: number;
  last_cheat_meal_date?: string | null;
  // MG_HEADKEEPS: разрешение главе семьи править и удалять мои записи.
  // Добавлять он может и без него — такие записи помечены короной.
  head_may_edit_diary?: boolean;
}
// Freemium: остаток квоты на генерацию меню (limit=null → безлимит/premium).
export interface MenuQuota {
  used: number;
  limit: number | null;
  reset_at: string; // YYYY-MM-DD
}
export interface SubscriptionStatus {
  is_active_premium: boolean;
  has_ever_premium: boolean;
  plan_code: string | null;
  status: string | null;
  expires_at: string | null;
  menu_quota: MenuQuota;
}
export interface User {
  id: number; name: string; email?: string; email_verified?: boolean; phone?: string;
  vk_id?: string; avatar_url?: string; user_type: string;
  allergies: string[]; disliked_products: string[]; profile?: UserProfile;
  ui_skin?: 'main' | 'second'; // MG_SKIN
  subscription_status?: SubscriptionStatus | null; // Freemium
  is_staff?: boolean;        // MG_CONSTRUCTOR: доступ к инструментам админа
  is_specialist?: boolean;   // MG_CONSTRUCTOR: доступ к инструментам специалиста
  created_at: string;
}

// ── MG_CONSTRUCTOR: ручной конструктор меню ──────────────────────────────────
export interface ConstructorRecipeMini {
  id: number; title: string; image_url?: string | null;
}
export interface ConstructorProductMini {
  id: number; name: string; image_url?: string | null; category_name?: string | null;
}
export interface ConstructorItemNutrition {
  calories?: number | null; proteins?: number | null; fats?: number | null; carbs?: number | null;
}
export interface ConstructedMealItem {
  id?: number;
  recipe?: ConstructorRecipeMini;   // на чтение (позиция-рецепт)
  recipe_id?: number;               // на запись
  product?: ConstructorProductMini; // на чтение (позиция-продукт)
  product_id?: number;              // на запись
  grams?: number | null;            // порция продукта, г
  nutrition?: ConstructorItemNutrition | null; // КБЖУ продукта на порцию
  quantity: number;
}
export interface ConstructedMeal {
  id?: number;
  day_index: number;
  order: number;
  name: string;
  target_calories?: number | null;
  target_protein?: string | number | null;
  target_fat?: string | number | null;
  target_carbs?: string | number | null;
  items: ConstructedMealItem[];
}
export type ConstructedMenuStatus = 'draft' | 'published';
export interface ConstructedMenu {
  id: number;
  name: string;
  author_name?: string | null;
  client_family?: number | null;
  days: number;
  status: ConstructedMenuStatus;
  meals: ConstructedMeal[];
  created_at: string;
  updated_at: string;
}
export interface ConstructedMenuListItem {
  id: number;
  name: string;
  author_name?: string | null;
  client_family?: number | null;
  client_family_name?: string | null;
  days: number;
  status: ConstructedMenuStatus;
  meals_count: number;
  created_at: string;
  updated_at: string;
}
export interface ConstructorClient { id: number; name: string; }

// ── MG_LEGAL: реквизиты + оферта + логотип ───────────────────────────────────
export interface LegalInfo {
  company_name: string;
  inn: string;
  ogrnip: string;
  legal_address: string;
  email: string;
  phone: string;
  bank_name: string;
  bank_bik: string;
  bank_account: string;
  corr_account: string;
  requisites_extra: string;
  offer_text: string;
  privacy_text: string; // MG_PRIVACY: политика обработки ПД
  logo_url: string | null;
  updated_at: string;
}
export interface Ingredient { name: string; quantity?: string; unit?: string; }
export interface RecipeStep { text: string; photo?: string; }
// MG_GALLERY: дополнительное фото блюда (загружается админом в карточке рецепта)
export interface RecipeGalleryPhoto { id: number; url: string; caption?: string; }
export interface NutritionValue { value: string; unit: string; }
export interface Nutrition {
  calories?: NutritionValue; proteins?: NutritionValue;
  fats?: NutritionValue; carbs?: NutritionValue;
  fiber?: NutritionValue; weight?: NutritionValue;
}
export type FoodGroup    = 'grain' | 'protein' | 'vegetable' | 'fruit' | 'dairy' | 'oil' | 'other';
export type ProteinType  = 'animal' | 'plant' | 'mixed';
export type GrainType    = 'whole' | 'refined';
export type CookingMethod = 'boiled' | 'baked' | 'fried' | 'grilled' | 'raw' | 'stewed' | 'steamed';  // MG_501_V_types
export type SuitableMeal = 'breakfast' | 'lunch' | 'dinner' | 'snack';
export interface Recipe {
  id: number; title: string; cook_time?: string; servings?: number;
  ingredients: Ingredient[]; steps: RecipeStep[]; nutrition: Nutrition;
  categories: string[]; image_url?: string; video_url?: string;
  gallery?: RecipeGalleryPhoto[];              // MG_GALLERY: доп. фото блюда
  country?: string; is_custom: boolean; author_name?: string; created_at: string;
  food_group?: FoodGroup | null;
  allergens?: string[];                        // MG_ALLERGEN14: ключи аллергенов
  suitable_for?: SuitableMeal[];
  protein_type?: ProteinType | null;
  grain_type?: GrainType | null;
  is_fatty_fish?: boolean;
  is_red_meat?: boolean;
  cooking_method?:     CookingMethod | null;  // MG_501_V_types
  has_added_sugar?:    boolean;
  oil_tsp?:            string | number | null;
  serving_size_label?: string | null;
  // Надёжные числовые КБЖУ-поля (в обход рассогласованного nutrition JSON).
  portion_g?:          number | null;        // вес одной порции, г
  kcal?:               string | number | null;  // на 1 порцию
  proteins?:           string | number | null;  // на 1 порцию, г
  fats?:               string | number | null;  // на 1 порцию, г
  carbs?:              string | number | null;  // на 1 порцию, г
  kcal_per_100g?:      string | number | null;
  proteins_per_100g?:  string | number | null;
  fats_per_100g?:      string | number | null;
  carbs_per_100g?:     string | number | null;
}
export type MealType = 'breakfast' | 'lunch' | 'dinner' | 'snack';
export const MEAL_LABELS: Record<MealType, string> = {
  breakfast: 'Завтрак', lunch: 'Обед', dinner: 'Ужин', snack: 'Перекус',
};
export type ComponentRole =
  | 'protein' | 'grain' | 'vegetable' | 'fruit' | 'dairy' | 'oil' | 'other';

export type MealSlot =
  | 'breakfast' | 'lunch' | 'dinner' | 'snack1' | 'snack2';

// MG_MEALSLOT: точное место приёма в дне. Перекусов два — при пяти приёмах они
// в разное время и с разной едой, и в одной куче читаются плохо.
export const MEAL_SLOT_LABELS: Record<MealSlot, string> = {
  breakfast: 'Завтрак',
  snack1: 'Перекус 1',
  lunch: 'Обед',
  snack2: 'Перекус 2',
  dinner: 'Ужин',
};
// Порядок — по ходу дня, а не по важности: дневник читают сверху вниз, как день.
export const MEAL_SLOT_ORDER: MealSlot[] = ['breakfast', 'snack1', 'lunch', 'snack2', 'dinner'];
// Цвет приёма. Те же значения, что в приложении (DIARY_COLOR): человек видит
// один и тот же дневник на телефоне и в браузере, и разные цвета читались бы
// как разные вещи. Два перекуса намеренно разного цвета — иначе разделение,
// ради которого всё делалось, пропадает именно там, где оно нужнее всего.
export const MEAL_SLOT_COLORS: Record<MealSlot, string> = {
  breakfast: '#FB8C00', // оранжевый
  snack1: '#8E24AA', // фиолетовый
  lunch: '#43A047', // зелёный
  snack2: '#6D4C41', // коричневый
  dinner: '#3949AB', // синий
};
// При трёх приёмах перекусов в раскладке нет — показывать пустыми незачем.
export const MEAL_SLOTS_BY_PLAN: Record<'3' | '5', MealSlot[]> = {
  '3': ['breakfast', 'lunch', 'dinner'],
  '5': MEAL_SLOT_ORDER,
};

export const COMPONENT_ROLE_LABELS: Record<ComponentRole, string> = {
  protein:   'Белок',
  grain:     'Крупа/гарнир',
  vegetable: 'Овощи',
  fruit:     'Фрукт',
  dairy:     'Молочное',
  oil:       'Масло',
  other:     'Прочее',
};

export const COMPONENT_ROLE_ICONS: Record<ComponentRole, string> = {
  protein:   '🍗',
  grain:     '🌾',
  vegetable: '🥗',
  fruit:     '🍎',
  dairy:     '🥛',
  oil:       '🫒',
  other:     '🍽',
};

export interface MenuItem {
  id: number; day_offset: number; meal_type: MealType;
  meal_slot?: MealSlot | string;
  component_role?: ComponentRole;
  recipe: Recipe; member?: number | null; member_name?: string; quantity: number; // MG_FAMILYGEN: member
  is_cheat_meal?: boolean; // MG_505_V_types
  // MG_WRITEOFF: списаны ли продукты этого блюда из холодильника. Без этого
  // поля кнопка «Приготовил» врёт: человек обновил страницу — и она снова
  // выглядит ненажатой, хотя продукты уже ушли.
  is_cooked?: boolean;
  // MG_PRODDISH: позиция-продукт (recipe тогда синтетический, с флагом is_product)
  product?: { id: number; name: string; image_url?: string | null; category_name?: string | null } | null;
  grams?: number | null;
}
export interface Menu {
  creator_name?: string; // MG_B09
  id: number; start_date: string; end_date: string; period_days: number;
  status: string; filters_used: Record<string, unknown>;
  generated_at: string; updated_at: string; items: MenuItem[];
  my_member_id?: number | null; is_head?: boolean; // MG_FAMILYGEN
  // MG_ITEMDEL: меню тронуто руками — заменой или удалением блюда. Нужно,
  // чтобы сказать, что цифры генератора больше не те.
  modified_by?: 'user' | 'specialist' | null;
}
export interface ShoppingItem {
  id: number; name: string; quantity?: number; unit?: string;
  category?: string; is_purchased: boolean;
}
export interface ShoppingList { id: number; items: ShoppingItem[]; created_at: string; }
// MG_204_V_types = 1
export interface FamilyMember {
  id: number; user_id: number; name: string; email?: string;
  avatar_url?: string; role: 'head' | 'member' | 'owner'; joined_at: string;
  allergies?: string[];
  disliked_products?: string[];
  is_managed?: boolean; // MG_MANAGEDMEMBER
  profile?: UserProfile | null;
}
export interface Family {
  id: number; name: string; owner_name: string;
  currency?: string; // MG_RUBRIC007
  // MG_SHELFLIFE: подставлять ли сроки годности при переносе покупок.
  // Настройка семейная, а не личная: холодильник общий, и режим не должен
  // зависеть от того, кто сегодня разбирает пакеты.
  auto_expiry?: boolean;
  members: FamilyMember[]; created_at: string;
  // MG_FAMINVITE: кого позвали и кто ещё не ответил. Без этого приглашение
  // выглядит как «нажал, и ничего не произошло»: в участниках человек
  // появляется только после согласия.
  pending_invites?: PendingInvite[];
}
// MG_FAMINVITE
export interface PendingInvite {
  id: number; name: string; email?: string | null; phone?: string | null;
  created_at: string;
}
export interface FamilyInvite {
  id: number; family_id: number; family_name: string;
  invited_by_name: string; members_count: number;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled';
  created_at: string;
}
// MG_ACTIVEFAMILY: один из столов, за которыми человек состоит.
export interface FamilyChoice {
  id: number; name: string; role: 'head' | 'member';
  is_active: boolean; is_own: boolean;
  members_count: number; has_premium: boolean;
}
export interface SubscriptionPlan {
  id: number; code: string; name: string; price: string;
  period: 'month' | 'year'; features: Record<string, unknown>;
  max_family_members: number;
}
export interface Subscription {
  id: number; plan: SubscriptionPlan; status: string;
  started_at: string; expires_at: string; auto_renew: boolean;
}
// MG_PAYPERIOD: вариант покупки — период и цена. Выгоду считает бэкенд,
// чтобы она была одинаковой в вебе и в мобильном.
export interface PlanOffer {
  code: string; title: string; months: number; price: string;
  price_per_month: string; discount_percent: number; plan_code: string;
}
export interface PaymentStatus {
  id: number; payment_id: string; amount: string; currency: string;
  status: 'pending' | 'succeeded' | 'cancelled' | 'refunded';
  offer_title: string | null; expires_at: string | null;
  paid_at: string | null; created_at: string;
}
export interface PaginatedResponse<T> {
  count: number; next?: string; previous?: string; results: T[];
}
export interface AuthTokens { access: string; refresh: string; }

// MG_EMAILVERIFY: результат регистрации (подтверждение e-mail по ссылке из письма)
export interface RegisterResult {
  detail: string;
  email?: string;
  requires_email_verification?: boolean;
  verify_link?: string;    // только на dev (DEBUG), когда почта не настроена
  access?: string;         // телефонная регистрация — сразу токены
  refresh?: string;
}

// MG_205UI_V_types = 1
export type TargetSource = 'auto' | 'user' | 'specialist';

export interface TargetMeta {
  source: TargetSource;
  by_user: { id: number; name: string } | null;
  at: string | null;
}

export type TargetField =
  | 'calorie_target'
  | 'protein_target_g'
  | 'fat_target_g'
  | 'carb_target_g'
  | 'fiber_target_g';

export type TargetsMeta = Partial<Record<TargetField, TargetMeta>>;

export interface TargetAuditEntry {
  id: number;
  field: TargetField;
  source: TargetSource;
  old_value: string | null;
  new_value: string | null;
  reason: string;
  at: string;
  by_user: { id: number; name: string } | null;
}

// ── Fridge ──────────────────────────────────────────────────────────────────
export interface Product {
  id: number;
  name: string;
  category?: string;                // legacy free-form string
  category_id?: number | null;
  category_slug?: string | null;
  category_name?: string | null;
  category_icon?: string | null;
  category_color?: string | null;
  default_unit?: string;
  // MG_FAMWEIGHT: сколько граммов в «штуке» и «упаковке» этого товара —
  // {единица: граммы}. Своё значение семьи уже подставлено вместо общего.
  unit_weights?: Record<string, string>;
  calories_per_100g?: string | number | null;
  nutrition?: Record<string, number>;
  barcode?: string | null;
  image_url?: string | null;
  is_seed?: boolean;
  is_own?: boolean;   // MG_PRODFAMILY: продукт нашей семьи (не из общего каталога)
  fridge_usage?: number | null; // MG_MYPRODUCTS: сколько позиций холодильника ссылается
}
export interface FridgeItem {
  id: number;
  product?: number | null;
  product_name?: string | null;
  product_category?: string | null;          // legacy
  product_category_id?: number | null;
  product_category_slug?: string | null;
  product_category_name?: string | null;
  product_category_icon?: string | null;
  product_category_color?: string | null;
  product_image_url?: string | null;
  name: string;
  quantity?: string | number | null;
  unit?: string;
  expiry_date?: string | null;
  created_at: string;
  updated_at: string;
}
export interface BarcodeLookupResult extends Product {
  // MG_SCANSRC: 'ai' — товара не нашлось в OpenFoodFacts и его «опознала»
  // модель по коду. Такие данные показываем с оговоркой и не пускаем в общий
  // каталог продуктов.
  source: 'local' | 'openfoodfacts' | 'ai';
  low_confidence?: boolean;
}

export interface FridgeMenuUsageRecipe {
  recipe_id: number;
  title: string;
  times: number;
}
export interface FridgeMenuUsage {
  count: number;
  period_days: number;
  recipes: FridgeMenuUsageRecipe[];
}
export interface FridgeItemDetailsResponse {
  item: FridgeItem;
  product: Product | null;
  days_left: number | null;
  usage_30d: FridgeMenuUsage;
}

// ── MG-609 ────────────────────────────────────────────────────────────────
export interface ProductCategory {
  id: number;
  slug: string;
  name_ru: string;
  name_en?: string;
  icon?: string;
  color?: string;
  sort_order?: number;
}

export interface FridgeHistoryItem {
  name: string;
  product_id: number | null;
  category_id: number | null;
  category_slug: string;
  category_name: string;
  category_icon: string;
  category_color: string;
  default_unit: string;
  image_url: string;
  times_used: number;
  last_used: string | null;
}

export interface RecognizedProduct {
  name: string;
  category: string;
  quantity: string | null;
  confidence: number;
  category_id: number | null;
  category_slug: string | null;
  category_name: string | null;
  category_icon: string | null;
  category_color: string | null;
}
export interface RecognizePhotoResponse {
  mode: 'single' | 'multi';
  product?: RecognizedProduct | null;
  products?: RecognizedProduct[];
  raw_text: string;
}

// DIARY_V2 types
export type DiaryNutrition = Partial<Record<'calories' | 'proteins' | 'fats' | 'carbs' | 'sugars' | 'fiber', NutritionValue>>;
export interface DiaryEntry {
  id: number;
  date: string;
  meal_type: MealType;
  meal_slot?: MealSlot; // MG_MEALSLOT: точное место приёма в дне
  recipe?: number | null;
  recipe_title?: string | null;
  custom_name?: string;
  nutrition: DiaryNutrition;
  quantity: number;
  // MG_DIARYGRAMS: вес порции, г. null — вес неизвестен (старая запись или
  // порционная: «1 порция супа» весом не меряется).
  grams?: number | null;
  planned_menu_item?: number | null;
  is_eaten: boolean;
  is_planned?: boolean; // DIARY_COPY_V3
  // MG_HEADKEEPS: кто внёс запись, если не сам владелец. Пусто — внёс сам;
  // так у всех записей, сделанных до появления этой возможности.
  added_by?: number | null;
  added_by_name?: string | null;
  created_at?: string;
}
export interface DiaryNutritionBucket {
  calories: number; proteins: number; fats: number; carbs: number;
}
export interface DiaryDayStats {
  date: string;
  planned: DiaryNutritionBucket;
  actual: DiaryNutritionBucket;
  total: DiaryNutritionBucket;
}
export interface DiaryWaterLog {
  id?: number | null;
  date: string;
  water_ml: number;
  added_by?: number | null; // MG_HEADKEEPS
  added_by_name?: string | null;
}

// MG_SHOP002_web_types — shopping lists v2
export type ShoppingV2Source = 'empty' | 'menu' | 'fridge' | 'ai_text' | 'csv';

// MG_RUBRIC004: rubricator search types.
export interface ShoppingV2RubricResult {
  product_id: number;
  name: string;
  unit: string;
  category_slug: string;
  category_name: string;
  subcategory: string;
}
export interface ShoppingV2RubricSuggestion {
  category_slug: string;
  category_name: string;
}
export interface ShoppingV2RubricResponse {
  query: string;
  results: ShoppingV2RubricResult[];
  suggestion: ShoppingV2RubricSuggestion | null;
}
export interface ShoppingV2Item {
  id: number;
  product_id?: number | null;
  category_slug?: string | null;
  category_name?: string | null;
  name: string;
  quantity?: number | null;
  unit?: string;
  category?: string;
  price_per_unit?: string | null; // MG_RUBRIC008
  line_total?: string | null;
  in_fridge?: boolean; // MG_SHOP2FRIDGE
  fridge_eligible?: boolean; // MG_SHOP2FRIDGE (non-food excluded)
  // MG_SHELFLIFE: предполагаемый «годен до» по справочнику — предположение,
  // которое показывают в окне переноса и правят до сохранения.
  suggested_expiry?: string | null;
  note?: string; // MG_SHOPNOTE
  image?: string | null; // MG_SHOPIMG resolved url
  image_url?: string | null; // MG_SHOPIMG raw url
  is_purchased: boolean;
  purchased_by?: number | null;
  purchased_by_name?: string | null;
  purchased_at?: string | null;
  sort_order?: number;
}

export interface ShoppingV2Capabilities {
  read: boolean;
  toggle: boolean;
  export: boolean;
  manage: boolean;
  pending?: boolean; // MG_SHAREACCEPT
}

export interface ShoppingV2ListBrief {
  id: number;
  name: string;
  source: ShoppingV2Source;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  items_total?: number;
  items_purchased?: number;
}

export interface ShoppingV2List extends ShoppingV2ListBrief {
  currency?: string; // MG_RUBRIC008
  total_price?: string | null; // MG_RUBRIC008
  menu?: number | null;
  created_by?: number | null;
  created_by_name?: string | null;
  archived_at?: string | null;
  items: ShoppingV2Item[];
  capabilities?: ShoppingV2Capabilities;
}

export interface ShoppingV2Access {
  id: number;
  user: number;
  user_email: string;
  user_name?: string | null;
  can_read: boolean;
  can_toggle: boolean;
  can_export: boolean;
  status?: 'pending' | 'accepted' | 'rejected'; // MG_SHAREACCEPT
  granted_at: string;
}

// MG_SHAREACCEPT: a list shared TO the current user, awaiting accept/reject.
export interface ShoppingV2PendingList {
  id: number;
  name: string;
  source: ShoppingV2Source;
  items_total?: number;
  items_purchased?: number;
  shared_by_name?: string | null;
  granted_at?: string | null;
}

export interface ShoppingV2ExportCategory {
  category: string;
  items: {
    name: string;
    quantity: string | null;
    unit: string;
    price_per_unit?: string | null; // MG_PRINTWEB001
    line_total?: string | null; // MG_PRINTWEB001
    is_purchased: boolean;
  }[];
}

export interface ShoppingV2ExportData {
  title: string;
  created_at: string;
  currency?: string; // MG_PRINTWEB001
  total_price?: string | null; // MG_PRINTWEB001
  categories: ShoppingV2ExportCategory[];
}

export interface ShoppingV2HistoryEntry {
  id: number;
  name: string;
  quantity?: number | null;
  unit?: string;
  category?: string;
  price_per_unit?: string | null; // MG_HISTWEB001
  purchased_by?: number | null;
  purchased_by_name?: string | null;
  purchased_at: string;
  source_list_id?: number | null;
}


// ─────────────────────────────────────────────────────────────────────────────
// MG_206_V_types: KBJU calculator
// ─────────────────────────────────────────────────────────────────────────────
export type KBJUSystem =
  | 'mifflin'
  | 'harris_benedict'
  | 'rospotrebnadzor'
  | 'efsa'
  | 'custom';

export type KBJUDiet = 'balanced' | 'high_protein' | 'low_carb';

export type KBJUCustomMode = 'grams' | 'percents';

export interface KBJURequest {
  system: KBJUSystem;
  diet?: KBJUDiet | null;
  // base inputs
  height_cm?: number;
  weight_kg?: number | string;
  birth_year?: number;
  gender?: 'male' | 'female' | 'other';
  activity_level?: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
  goal?: 'lose_weight' | 'maintain' | 'gain_weight' | 'healthy';
  // custom
  custom_mode?: KBJUCustomMode | null;
  custom_calorie_target?: number;
  custom_protein_g?: number | string;
  custom_fat_g?: number | string;
  custom_carb_g?: number | string;
  custom_fiber_g?: number | string;
  custom_calorie_delta?: number;
  custom_protein_pct?: number;
  custom_fat_pct?: number;
  custom_carb_pct?: number;
}

export interface KBJUResult {
  system: KBJUSystem;
  diet?: KBJUDiet | null;
  age?: number | null;
  bmr?: number | null;
  tdee?: number | null;
  calorie_target: number;
  protein_target_g: string;
  fat_target_g: string;
  carb_target_g: string;
  fiber_target_g: string;
}

// MG_APKSITE: сборка приложения, выложенная на сайте, пока идёт модерация в
// магазине. Пусто — выкладывать нечего, блок скачивания не показывается.
export interface AndroidBuild {
  version_name: string;
  version_code?: number | null;
  url: string | null;
  size_bytes?: number;
  sha256?: string;
  notes?: string;
  created_at?: string;
}

// ── MG_WRITEOFF: списание продуктов из холодильника ─────────────────────────

/** Строка списания: что ушло из холодильника. */
export interface WriteOffLine {
  name: string;
  product_id: number | null;
  fridge_item_id?: number | null;
  quantity: string;
  unit: string;
}

/** Ответ на «Приготовил»: что списалось и чего не хватило. */
export interface CookedResult {
  write_off_id: number;
  /** false — блюдо уже было отмечено, второй раз ничего не списалось. */
  created: boolean;
  written_off: WriteOffLine[];
  shortfall: WriteOffLine[];
}

/** Ответ на ручное списание позиции холодильника («Израсходовал»). */
export interface ConsumeResult {
  write_off_id: number;
  item: FridgeItem;
}
